import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  Clock,
  ArrowRight,
  Phone,
  Calendar
} from 'lucide-react';
import api from '@/utils/api';
import { format } from 'date-fns';

const AdminOverview = () => {
  const [stats, setStats] = useState({
    totalProperties: 0,
    totalLeads: 0,
    featuredProperties: 0,
    todayLeads: 0,
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [propertiesRes, leadsRes] = await Promise.all([
        api.get('/properties'),
        api.get('/leads'),
      ]);

      const properties = propertiesRes.data;
      const leads = leadsRes.data;

      // Calculate today's leads
      const today = new Date().toDateString();
      const todayLeads = leads.filter(
        (lead) => new Date(lead.created_at).toDateString() === today
      ).length;

      setStats({
        totalProperties: properties.length,
        totalLeads: leads.length,
        featuredProperties: properties.filter((p) => p.featured).length,
        todayLeads,
      });

      setRecentLeads(leads.slice(0, 5));
      setRecentProperties(properties.slice(0, 4));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Properties',
      value: stats.totalProperties,
      icon: Building2,
      color: 'rose-gold',
      link: '/admin/properties',
    },
    {
      title: 'Total Leads',
      value: stats.totalLeads,
      icon: Users,
      color: 'green-500',
      link: '/admin/leads',
    },
    {
      title: 'Featured Properties',
      value: stats.featuredProperties,
      icon: TrendingUp,
      color: 'blue-500',
      link: '/admin/properties',
    },
    {
      title: "Today's Inquiries",
      value: stats.todayLeads,
      icon: Clock,
      color: 'yellow-500',
      link: '/admin/leads',
    },
  ];

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white/60">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-4xl font-bold text-white mb-2">Dashboard Overview</h1>
        <p className="text-white/60">Welcome back! Here's what's happening with Propvanta.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={stat.link}
              className="block glass-effect border border-dark-lighter rounded-xl p-6 hover:border-rose-gold/50 transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg bg-${stat.color}/10`}>
                  <stat.icon className={`text-${stat.color}`} size={24} />
                </div>
                <ArrowRight 
                  className="text-white/20 group-hover:text-rose-gold group-hover:translate-x-1 transition-all" 
                  size={20} 
                />
              </div>
              <p className="text-white/60 text-sm uppercase tracking-wider mb-1">{stat.title}</p>
              <p className="font-heading text-4xl font-bold text-white">{stat.value}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Leads */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-effect border border-dark-lighter rounded-xl overflow-hidden"
        >
          <div className="p-6 border-b border-dark-lighter flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold text-white">Recent Inquiries</h2>
            <Link 
              to="/admin/leads" 
              className="text-rose-gold text-sm hover:underline flex items-center gap-1"
            >
              View All <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-dark-lighter">
            {recentLeads.length > 0 ? (
              recentLeads.map((lead) => (
                <div key={lead.id} className="p-4 hover:bg-dark/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-white/60 text-sm flex items-center gap-2 mt-1">
                        <Phone size={12} />
                        {lead.phone}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-2 py-1 bg-rose-gold/10 text-rose-gold text-xs rounded-full">
                        {lead.lead_type || 'inquiry'}
                      </span>
                      <p className="text-white/40 text-xs mt-2 flex items-center gap-1 justify-end">
                        <Calendar size={10} />
                        {format(new Date(lead.created_at), 'MMM dd, HH:mm')}
                      </p>
                    </div>
                  </div>
                  {lead.message && (
                    <p className="text-white/50 text-sm mt-2 line-clamp-1">{lead.message}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-white/40">
                No leads yet. Start generating leads from your website!
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Properties */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-effect border border-dark-lighter rounded-xl overflow-hidden"
        >
          <div className="p-6 border-b border-dark-lighter flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold text-white">Recent Properties</h2>
            <Link 
              to="/admin/properties" 
              className="text-rose-gold text-sm hover:underline flex items-center gap-1"
            >
              View All <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-dark-lighter">
            {recentProperties.length > 0 ? (
              recentProperties.map((property) => (
                <div key={property.id} className="p-4 hover:bg-dark/50 transition-colors">
                  <div className="flex gap-4">
                    <img
                      src={property.images[0]}
                      alt={property.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{property.title}</p>
                      <p className="text-white/60 text-sm">{property.builder}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-rose-gold font-semibold">{formatPrice(property.price)}</p>
                        <span className={`text-xs ${property.status === 'Available' ? 'text-green-400' : 'text-red-400'}`}>
                          {property.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-white/40">
                No properties yet. Add your first property!
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="glass-effect border border-dark-lighter rounded-xl p-6"
      >
        <h2 className="font-heading text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/admin/properties"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Building2 size={18} />
            Add New Property
          </Link>
          <Link
            to="/admin/leads"
            className="btn-outline inline-flex items-center gap-2"
          >
            <Users size={18} />
            View All Leads
          </Link>
          <Link
            to="/"
            target="_blank"
            className="px-6 py-3 border border-dark-lighter text-white/60 rounded-full hover:border-rose-gold/50 hover:text-white transition-all inline-flex items-center gap-2"
          >
            View Website
            <ArrowRight size={16} />
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminOverview;
